import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedNumericOperators,
  advancedEnumOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { chipStyles, formatPrice, getDate, getFormattedDate, formatPercent } from '../../../utils/helpers';
import { getCommissionsInfoByType } from '../../../store/GeneralStore';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { CustomerCombinedAsset } from '../../../store/CustomerCombinedAsset';
import { addDrawer } from '../../../store/Drawer';
import { Icon } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { getAssetById } from '../../../store/Asset';
import { getProvisionalAssetById } from '../../../store/ProvisionalAsset';

const handleAssetClick = async (row: CustomerCombinedAsset, type: ComponentName) => {
  const asset = row.type === 'asset' ? await getAssetById(row.id) : await getProvisionalAssetById(row.id);
  return addDrawer({
    id: v4(),
    data: asset,
    ...getMetaData({
      name: type,
      sfId: row.assetSfId || undefined,
      id: asset.id,
      data: asset
    })
  });
};

export const CustomerCombinedAssetColumnDefs = (tabIndex: number): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assetNumber',
    headerName: 'Number',
    sortable: true,
    filterable: true,
    minWidth: 160,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    quickFilter: true,
    pinned: 'left',
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      const type = params.row.type === 'asset' ? ComponentName.ASSET : ComponentName.PROVISIONAL_ASSET;
      return (
        <button
          className="flex items-center"
          onClick={() => handleAssetClick(params.row, type)}>
          <Icon
            className="p-3 mr-1"
            type={params.row.type === 'asset' ? 'assetMed' : 'provisionalAssetMed'}
          />
          <Text
            size="sm14"
            color="indigo">
            {params?.row.assetNumber as string}
          </Text>
        </button>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    align: 'center',
    field: 'commissionStatus',
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'serviceStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'serviceStatus',
    headerName: 'Svc. Status',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: tabIndex === 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.chargeRecurringExpected',
    headerName: 'Exp. MRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.chargeRecurringExpected,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.chargeRecurringActual',
    headerName: 'Act. MRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.chargeRecurringActual,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.revenueRecurringExpected',
    headerName: 'Exp. MRR',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.revenueRecurringExpected,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.revenueRecurringActual',
    headerName: 'Act. MRR',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.revenueRecurringActual,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.chargeOnetimeExpected',
    headerName: 'Exp. NRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.chargeOnetimeExpected,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.chargeOnetimeActual',
    headerName: 'Act. NRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.chargeOnetimeActual,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'attribution.revenueRecurringActualPercent',
    headerName: 'Act. %',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value, row) => row.attribution?.revenueRecurringActualPercent,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPercent(params.value as number, true)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualDecommissionedDate',
    headerName: 'Act. Decom.',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 130,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: tabIndex !== 2
  },
  {
    field: 'decommissionReason',
    headerName: 'Decom. Reason',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    filterOperators: advancedOperators(),
    hideByDefault: tabIndex !== 2
  },
  {
    field: 'legacyArchiveReason',
    headerName: 'Leg. Reason',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    filterOperators: advancedOperators(),
    hideByDefault: tabIndex !== 3
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedFirstCommissionRun',
    headerName: 'Exp. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 130,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: tabIndex !== 1
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualFirstCommissionRun',
    headerName: 'Act. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 130,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionableMrc',
    headerName: 'Exp. MRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex === 0 || tabIndex === 3
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedGrossMrr',
    headerName: 'Exp. MRR',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex === 0 || tabIndex === 3
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionableNrc',
    headerName: 'Exp. NRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 2 && tabIndex !== 4
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'recurringCommission',
    headerName: 'Exp. %',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPercent(params.value as number, true)}
        </span>
      );
    },
    hideByDefault: tabIndex === 0 || tabIndex === 3
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'mrc',
    headerName: 'MRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerCombinedAsset>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    hideByDefault: tabIndex !== 3
  },
  {
    field: 'linkCount',
    headerName: 'Link Count',
    sortable: true,
    filterable: true,
    flex: 0.6,
    minWidth: 80,
    type: 'number',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'salesAgents'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'salesAgentName',
    headerName: 'Sales Agent',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: tabIndex === 0
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'suppliers'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'serviceStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'serviceStatus',
    headerName: 'Svc. Status',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: tabIndex !== 0
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedDecommissionDate',
    headerName: 'Exp. Decom.',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: tabIndex !== 0
  },
  {
    field: 'expectedAccountNumber',
    headerName: 'Exp. Acct. Number',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    filterOperators: advancedOperators(),
    hideByDefault: tabIndex !== 1
  },
  {
    field: 'productName',
    headerName: 'Product',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    filterOperators: advancedOperators(),
    hideByDefault: tabIndex < 3
  }
];
